// src/components/Users/UsersTable.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Avatar from '../Common/Avatar';
import Pagination from '../Common/Pagination';
import { useTranslation } from '../../contexts/LanguageContext';
import {
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  ShieldCheckIcon,
  XMarkIcon
} from "@heroicons/react/24/outline";

const UsersTable = ({
  users,
  currentPage,
  totalPages,
  total,
  onPageChange,
  loading,
  onVerificationClick,
  onFilter,
  onVerificationRequest
}) => {
  const { t, tStatus } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoadingVerification, setIsLoadingVerification] = useState(false);

  const handleApplyFilters = () => {
    if (onFilter) {
      onFilter({ byKeyword: searchTerm.trim() || undefined, limit: 100, currentPage: 1 });
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    if (onFilter) {
      onFilter({ limit: 100, currentPage: 1 });
    }
  };

  const handleVerificationRequest = async () => {
    setIsLoadingVerification(true);
    try {
      if (onVerificationRequest) {
        await onVerificationRequest();
      }
    } catch (error) {
      console.error('Error fetching verification requests:', error);
    } finally {
      setIsLoadingVerification(false);
    }
  };

  return (
    <div className="bg-white rounded-[10px] border border-dashboardStroke shadow-[0px_9px_15px_rgba(0,0,0,0.07)] overflow-hidden">
      {/* Dark Header */}
      <div className="bg-dashboardDark h-16 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-white font-semibold text-lg">{t("users.title")}</h2>
          <span className="text-white font-semibold">({total || 0})</span>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Search Input */}
          <div className="flex items-center gap-2 px-3 py-1.5 border border-white rounded">
            <MagnifyingGlassIcon className="w-[18px] h-[18px] text-white" />
            <input
              type="text"
              placeholder={t("users.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-transparent text-white placeholder-white/70 text-sm font-light outline-none w-32"
            />
          </div>
          
          {/* Apply Filters Button */}
          <button
            onClick={handleApplyFilters}
            className="flex items-center gap-2 px-4 py-1.5 bg-dashboardCoral rounded text-white text-sm font-medium hover:bg-dashboardCoral/90 transition-colors"
          >
            <FunnelIcon className="w-[18px] h-[18px]" />
            <span>{t("common.applyFilters")}</span>
          </button>
          
          {/* Verification Request Button */}
          <button
            onClick={handleVerificationRequest}
            disabled={isLoadingVerification}
            className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded text-white text-sm font-medium transition-colors"
          >
            <ShieldCheckIcon className="w-[18px] h-[18px]" />
            <span>{isLoadingVerification ? t("common.loading") : t("common.verificationRequest")}</span>
          </button>
          
          {/* Clear Button */}
          <button
            onClick={handleClearFilters}
            className="flex items-center gap-2 px-4 py-1.5 border border-white rounded text-white text-sm font-medium hover:bg-white/10 transition-colors"
          >
            <XMarkIcon className="w-[18px] h-[18px]" />
            <span>{t("common.clear")}</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dashboardCoral"></div>
          <span className="ml-3 text-dashboardText">{t("users.loading")}</span>
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-dashboardText">{t("users.noFound")}</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dashboardStroke">
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.srNo")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.name")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("users.col.userHandle")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.email")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("users.col.walletBalance")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("users.col.socialLogin")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("users.col.accountStatus")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("users.col.verificationStatus")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.createdAt")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => {
                  const verificationStatus = user.identityVerification?.status;
                  const isApproved = verificationStatus === 'approved';
                  const isPending = verificationStatus === 'pending' || (!verificationStatus && user.identityVerification);
                  
                  return (
                    <tr 
                      key={user._id}
                      className={`border-b border-dashboardStroke hover:bg-gray-50 transition-colors ${
                        index === 0 ? "shadow-[inset_3px_0px_0px_#FF7C17]" : ""
                      }`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                        {((currentPage - 1) * 10) + index + 1}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Avatar
                            src={user.profilePicture}
                            name={user.name}
                            size="sm"
                          />
                          <Link 
                            to={`/users/${user._id}`} 
                            state={{ userData: user }}
                            className="text-dashboardDarkText font-medium hover:text-dashboardCoral"
                          >
                            {user.name}
                          </Link>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                        {user.userHandle ? `@${user.userHandle}` : 'N/A'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                        {user.email || 'N/A'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-base font-medium text-dashboardDarkText">
                        {Number(user.walletBalance || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {user.socialMediaLogin?.provider === 'google' ? (
                          <div className="flex items-center gap-2">
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            <span className="text-sm text-gray-600">Google</span>
                          </div>
                        ) : user.socialMediaLogin?.provider === 'apple' ? (
                          <div className="flex items-center gap-2">
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                            </svg>
                            <span className="text-sm text-gray-600">Apple</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          user.accountStatus === 'active' ? 'bg-green-100 text-green-800' :
                          user.accountStatus === 'blocked' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {tStatus(user.accountStatus)}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {isPending ? (
                          <button
                            onClick={() => onVerificationClick && onVerificationClick(user)}
                            className="inline-flex px-2 py-1 text-xs font-medium rounded-full cursor-pointer hover:bg-yellow-200 transition-colors bg-yellow-100 text-yellow-800"
                          >
                            {t("common.pending")}
                          </button>
                        ) : isApproved ? (
                          <button
                            onClick={() => onVerificationClick && onVerificationClick(user)}
                            className="inline-flex px-2 py-1 text-xs font-medium rounded-full cursor-pointer hover:bg-green-200 transition-colors bg-green-100 text-green-800"
                          >
                            {t("common.approved")}
                          </button>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                            {t("status.na")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                        <div>{new Date(user.createdAt).toLocaleDateString()}</div>
                        <div className="text-xs text-gray-500">{new Date(user.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-0.5">
                          <Link 
                            to={`/users/${user._id}`} 
                            state={{ userData: user }}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                          >
                            <EyeIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                          </Link>
                          <div className="w-px h-6 bg-dashboardStroke"></div>
                          <button className="p-1 hover:bg-gray-100 rounded transition-colors">
                            <PencilIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                          </button>
                          <div className="w-px h-6 bg-dashboardStroke"></div>
                          <button className="p-1 hover:bg-gray-100 rounded transition-colors">
                            <TrashIcon className="w-6 h-6 text-dashboardCoral" strokeWidth={1.3} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {totalPages > 1 && (
            <div className="px-4 py-4 border-t border-dashboardStroke">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                total={total}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UsersTable;
